import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PageContext } from '@narvar/nth-hook-context-page';
import { GetProductRecommendationsSettingsQuery } from '../types';

enum CustomComponentEventType {
  WINDOW_ID = 'windowId',
  SETTINGS = 'settings',
}

interface CustomComponentEvent<T extends CustomComponentEventType, U> {
  type: T;
  data: U;
}

type WindowIdCustomComponentEvent = CustomComponentEvent<
  CustomComponentEventType.WINDOW_ID,
  {
    parentWindowId: string;
  }
>;

type SettingsComponentEvent = CustomComponentEvent<
  CustomComponentEventType.SETTINGS,
  CustomComponentStateSettings
>;

type CustomComponentEvents =
  | WindowIdCustomComponentEvent
  | SettingsComponentEvent;

export interface CustomComponentStateSettings {
  pageContext: PageContext;
  settings: Record<string, unknown> | null | undefined;
  queryData: GetProductRecommendationsSettingsQuery | null;
}

export interface CustomComponentState {
  state: CustomComponentStateSettings | null;
  contentLoadedCalledRef: boolean;
  signalContentLoaded: (() => void) | null;
}

const isCustomComponentEvent = (
  data: unknown,
): data is CustomComponentEvents => {
  if (typeof data !== 'object' || !data) return false;
  const possibleCustomComponentEvent = data as CustomComponentEvents;

  switch (possibleCustomComponentEvent.type) {
    case CustomComponentEventType.WINDOW_ID: {
      if (
        typeof possibleCustomComponentEvent.data.parentWindowId !== 'string'
      ) {
        return false;
      }
      break;
    }
    case CustomComponentEventType.SETTINGS: {
      if (typeof possibleCustomComponentEvent.data.pageContext !== 'object') {
        return false;
      }
      break;
    }
    default: {
      return false;
    }
  }

  return true;
};

const removeUnit = (cssSize: string): number =>
  (typeof cssSize === 'string' && parseFloat(cssSize.replace(/D/g, ''))) || 0;

const getDocumentSize = (): { height: number; width: number } => {
  const { scrollHeight: height, scrollWidth: width } = document.body;
  const {
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
  } = window.getComputedStyle(document.body);

  return {
    height:
      height +
      removeUnit(marginTop) +
      removeUnit(marginBottom) +
      removeUnit(borderTop) +
      removeUnit(borderBottom),
    width:
      width +
      removeUnit(marginLeft) +
      removeUnit(marginRight) +
      removeUnit(borderLeft) +
      removeUnit(borderRight),
  };
};

const useCustomComponentState = (): CustomComponentState => {
  const [parentWindowOrigin, setParentWindowOrigin] = useState('*');
  const [parentWindowId, setParentWindowId] = useState<string | null>(null);

  const [state, setState] = useState<CustomComponentStateSettings | null>(null);

  useEffect(() => {
    const cb = ({ origin, data }: MessageEvent) => {
      if (!isCustomComponentEvent(data)) {
        return;
      }

      setParentWindowOrigin(origin);

      switch (data.type) {
        case CustomComponentEventType.WINDOW_ID: {
          const { parentWindowId: newParentWindowId } = data.data;
          window.parent.postMessage(
            {
              guestReadyToReceiveMessages: true,
              parentWindowId: newParentWindowId,
            },
            origin,
          );
          setParentWindowId(newParentWindowId);
          break;
        }
        case CustomComponentEventType.SETTINGS: {
          setState(data.data);
          break;
        }
        default: {
          // NOTE: Exhaustive match
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
          ((_: never) => {})(data);
        }
      }
    };

    window.addEventListener('message', cb);

    return () => {
      window.removeEventListener('message', cb);
    };
  }, []);

  const checkDocumentHeightRef = useRef(true);
  const documentSizeRef = useRef({ height: 0, width: 0 });

  useEffect(() => {
    if (!parentWindowOrigin || !parentWindowId) return undefined;

    const checkDocumentHeight = () => {
      if (!checkDocumentHeightRef.current) return;

      const newDocumentSize = getDocumentSize();

      const { height: oldHeight, width: oldWidth } = documentSizeRef.current;
      const { height: newHeight, width: newWidth } = newDocumentSize;

      if (newHeight !== oldHeight || newWidth !== oldWidth) {
        documentSizeRef.current = newDocumentSize;
        window.parent.postMessage(
          { guestDocumentSize: newHeight, parentWindowId },
          parentWindowOrigin,
        );
      }

      window.requestAnimationFrame(checkDocumentHeight);
    };

    window.requestAnimationFrame(checkDocumentHeight);

    return () => {
      checkDocumentHeightRef.current = false;
    };
  }, [parentWindowOrigin, parentWindowId]);

  const contentLoadedCalledRef = useRef(false);

  const signalContentLoaded =
    !parentWindowOrigin || !parentWindowId
      ? null
      : () => {
          if (contentLoadedCalledRef.current) return;
          contentLoadedCalledRef.current = true;
          window.parent.postMessage(
            { guestContentLoaded: true, parentWindowId },
            parentWindowOrigin,
          );
        };

  return {
    state,
    contentLoadedCalledRef: contentLoadedCalledRef.current,
    signalContentLoaded,
  };
};

const CustomComponentStateContext = createContext<CustomComponentState>({
  state: null,
  contentLoadedCalledRef: false,
  signalContentLoaded: null,
});

export const CustomComponentStateContextProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const customComponentState = useCustomComponentState();

  return (
    <CustomComponentStateContext.Provider value={customComponentState}>
      {children}
    </CustomComponentStateContext.Provider>
  );
};

export const useCustomComponentStateContext = (): CustomComponentState =>
  useContext(CustomComponentStateContext);
